import type {
  FFmpegConfig,
  FileData,
  LogEvent,
  ProgressEvent,
  DirEntry,
  ExecOptions,
} from '@wasmedia/wffmpeg-types';
import { createSession } from './core/loader.js';
import type { CoreSession } from './core/session.js';
import { parseCommand } from './command/parser.js';
import { isBrowserMainThread } from './env/detect.js';

type EventMap = {
  log: LogEvent;
  progress: ProgressEvent;
};

type EventCallback<K extends keyof EventMap> = (event: EventMap[K]) => void;

interface PendingCall {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
}

interface WorkerInbound {
  id: number;
  type: 'result' | 'error' | 'log' | 'progress';
  data: unknown;
}

export class FFmpeg {
  /** Direct-mode handle. Set when running on the calling thread (Node, or browser inside an existing Worker). */
  private session: CoreSession | null = null;
  /** Worker-mode handle. Set in browsers when config.workerURL is provided. */
  private worker: Worker | null = null;
  private nextRequestId = 1;
  private pending = new Map<number, PendingCall>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private listeners = new Map<string, Set<(event: any) => void>>();
  private loaded = false;

  get isLoaded(): boolean {
    return this.loaded;
  }

  async load(config: FFmpegConfig = {}): Promise<void> {
    if (this.loaded) return;

    if (config.workerURL && typeof Worker !== 'undefined') {
      await this.loadInWorker(config);
    } else if (isBrowserMainThread()) {
      // The pthreaded core runs in a Worker. On the main thread every FS call
      // from FFmpeg's threads would be serviced by the UI thread, and the
      // setup is untested; refuse it rather than risk a hang.
      throw new Error(
        'FFmpegConfig.workerURL is required in a browser: the pthreaded core ' +
          'runs in a Worker. Pass workerURL, or call load() from inside a Worker.',
      );
    } else {
      await this.loadDirect(config);
    }

    this.loaded = true;

    if (config.log) {
      this.on('log', (e) => {
        // eslint-disable-next-line no-console
        console.log(`[ffmpeg ${e.type}]`, e.message);
      });
    }
  }

  private async loadInWorker(config: FFmpegConfig): Promise<void> {
    if (!config.coreURL) {
      throw new Error('FFmpegConfig.coreURL is required when workerURL is set.');
    }
    const worker = new Worker(config.workerURL!, { type: 'module' });
    worker.addEventListener('message', (e: MessageEvent<WorkerInbound>) =>
      this.onWorkerMessage(e.data),
    );
    worker.addEventListener('error', (e) => {
      // Surface bootstrap errors as a rejection on every pending call, and
      // drop the handle: a worker that errored will never answer again, so
      // leaving it in place would make later calls hang forever.
      this.disposeWorker(new Error(`Worker error: ${e.message ?? '(unknown)'}`));
    });
    this.worker = worker;
    await this.send('load', {
      coreURL: config.coreURL,
      wasmURL: config.wasmURL,
    });
  }

  private async loadDirect(config: FFmpegConfig): Promise<void> {
    this.session = await createSession({
      config,
      onLog: (type, message) => this.emit('log', { type, message }),
      onProgress: (progress, time) => this.emit('progress', { progress, time }),
    });
  }

  /**
   * Tear down worker mode, failing everything still in flight.
   *
   * Used when the Worker errors, when the caller terminates, and when an exec
   * is cancelled: a running FFmpeg main() cannot be interrupted, so
   * destroying the Worker (and with it, its pthread Workers) is the only way
   * to actually stop one. The instance
   * becomes unloaded, and load() must be called again before reuse.
   */
  private disposeWorker(reason: Error): void {
    const worker = this.worker;
    this.worker = null;
    this.loaded = false;
    for (const [, p] of this.pending) p.reject(reason);
    this.pending.clear();
    worker?.terminate();
  }

  private onWorkerMessage(msg: WorkerInbound): void {
    if (msg.type === 'log') {
      this.emit('log', msg.data as LogEvent);
      return;
    }
    if (msg.type === 'progress') {
      this.emit('progress', msg.data as ProgressEvent);
      return;
    }
    const pending = this.pending.get(msg.id);
    if (!pending) return;
    this.pending.delete(msg.id);
    if (msg.type === 'error') {
      pending.reject(new Error(String(msg.data)));
    } else {
      pending.resolve(msg.data);
    }
  }

  private send<T = unknown>(type: string, data: unknown, transfer?: Transferable[]): Promise<T> {
    if (!this.worker) {
      throw new Error('FFmpeg not running in worker mode.');
    }
    const worker = this.worker;
    const id = this.nextRequestId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      try {
        worker.postMessage({ id, type, data }, transfer ?? []);
      } catch (err) {
        this.pending.delete(id);
        reject(err);
      }
    });
  }

  async terminate(): Promise<void> {
    if (this.worker) {
      // No graceful round-trip: a running main() cannot be stopped by a
      // message, and terminate() exists to cut it short. Killing the Worker
      // ends it and its pthread Workers.
      this.disposeWorker(new Error('FFmpeg terminated.'));
    }
    this.session = null;
    this.loaded = false;
    this.pending.clear();
    this.listeners.clear();
  }

  private requireLoaded(): void {
    if (!this.loaded) throw new Error('FFmpeg not loaded. Call load() first.');
  }

  /** Execute FFmpeg with the given arguments array. */
  async exec(args: string[], timeout?: number, options?: ExecOptions): Promise<number> {
    this.requireLoaded();

    const signal = options?.signal;
    const effectiveTimeout = options?.timeout ?? timeout;

    if (signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }

    if (!this.worker) {
      // Direct mode: there is no Worker to destroy, and a running main()
      // cannot be interrupted, so a timeout or a later abort could only
      // abandon the run, not stop it. An already-aborted signal is handled
      // above; do not set up machinery implying more than that.
      return this.session!.run(args);
    }

    const id = this.nextRequestId++;

    return new Promise<number>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      let onAbort: () => void = () => {};

      // Every exit path runs this, so the pending entry cannot outlive the
      // call. Leaking one meant the map grew per timeout and the late result
      // was silently dropped.
      const settle = () => {
        if (timer) clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        this.pending.delete(id);
      };

      // Cancelling destroys the Worker -- see disposeWorker(). Nothing short
      // of that stops an in-flight main(), and leaving it running would pin a
      // core until the encode finished.
      const cancel = (err: Error) => {
        settle();
        this.disposeWorker(err);
        reject(err);
      };

      onAbort = () => cancel(new DOMException('Aborted', 'AbortError'));

      if (effectiveTimeout) {
        timer = setTimeout(
          () => cancel(new DOMException('Timeout', 'TimeoutError')),
          effectiveTimeout,
        );
      }
      signal?.addEventListener('abort', onAbort, { once: true });

      this.pending.set(id, {
        resolve: (value) => {
          settle();
          resolve(value as number);
        },
        reject: (reason) => {
          settle();
          reject(reason);
        },
      });

      try {
        this.worker!.postMessage({ id, type: 'exec', data: args });
      } catch (err) {
        settle();
        reject(err);
      }
    });
  }

  /** Parse and execute a CLI-style command string. */
  async run(command: string, timeout?: number): Promise<number> {
    const args = parseCommand(command);
    if (args[0] === 'ffmpeg') args.shift();
    return this.exec(args, timeout);
  }

  async writeFile(path: string, data: FileData): Promise<void> {
    this.requireLoaded();
    const u8 =
      data instanceof Uint8Array
        ? data
        : data instanceof ArrayBuffer
          ? new Uint8Array(data)
          : new TextEncoder().encode(data);

    if (this.worker) {
      // Copy, then transfer the copy. Transferring the caller's own buffer
      // would detach it, which is a surprising side effect of a write; the
      // copy keeps the caller's data usable at the cost of one allocation.
      const copy = new Uint8Array(u8.byteLength);
      copy.set(u8);
      await this.send('writeFile', { path, contents: copy }, [copy.buffer]);
      return;
    }
    this.session!.fs.writeFile(path, u8);
  }

  async readFile(path: string): Promise<Uint8Array> {
    this.requireLoaded();
    if (this.worker) return this.send<Uint8Array>('readFile', path);
    return this.session!.fs.readFile(path);
  }

  async deleteFile(path: string): Promise<void> {
    this.requireLoaded();
    if (this.worker) {
      await this.send('deleteFile', path);
      return;
    }
    this.session!.fs.unlink(path);
  }

  async rename(oldPath: string, newPath: string): Promise<void> {
    this.requireLoaded();
    if (this.worker) {
      await this.send('rename', { oldPath, newPath });
      return;
    }
    this.session!.fs.rename(oldPath, newPath);
  }

  async listDir(path: string): Promise<DirEntry[]> {
    this.requireLoaded();
    if (this.worker) return this.send<DirEntry[]>('readdir', path);
    const fs = this.session!.fs;
    const entries = fs.readdir(path);
    return entries
      .filter((name: string) => name !== '.' && name !== '..')
      .map((name: string) => {
        const stat = fs.stat(`${path}/${name}`);
        return { name, isDir: fs.isDir(stat.mode) };
      });
  }

  async createDir(path: string): Promise<void> {
    this.requireLoaded();
    if (this.worker) {
      await this.send('mkdir', path);
      return;
    }
    this.session!.fs.mkdir(path);
  }

  on<K extends keyof EventMap>(event: K, callback: EventCallback<K>): void {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(callback);
  }

  off<K extends keyof EventMap>(event: K, callback: EventCallback<K>): void {
    this.listeners.get(event)?.delete(callback);
  }

  private emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    const set = this.listeners.get(event);
    if (set) for (const cb of set) cb(data);
  }
}
